import { useMemo, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { apiGetDevice } from '@/features/devices/api/api-get-device'
import { apiGetDeviceHistory } from '@/features/devices/api/api-get-device-history'
import { DeviceHistoryList } from '@/features/devices/components/device-history-list'
import { emptyPagination } from '@/lib/api/empty-pagination'
import { LoadingSpinner, StatusBadge } from '@/shared/components'
import { useApiResource } from '@/shared/hooks/use-api-resource'

const BLANK_VALUE = '---'

type DeviceDetailRowProps = {
  label: string
  value: ReactNode
}

const DeviceDetailRow = ({ label, value }: DeviceDetailRowProps) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
    <dt className="min-w-28 text-sm font-semibold text-foreground">{label}</dt>
    <dd className="text-sm text-foreground">{value}</dd>
  </div>
)

export const DeviceShowPage = () => {
  const { id } = useParams<{ id: string }>()
  const [historyPage, setHistoryPage] = useState(1)

  const loadDevice = useMemo(() => (id ? () => apiGetDevice(id) : null), [id])
  const loadHistory = useMemo(
    () => (id ? () => apiGetDeviceHistory(id, historyPage) : null),
    [id, historyPage]
  )
  const deviceResource = useApiResource(loadDevice, 'Failed to load device')
  const historyResource = useApiResource(
    loadHistory,
    'Failed to load device history'
  )

  const device = deviceResource.data
  const isLoading = deviceResource.isLoading
  const error = id ? deviceResource.error : 'Device not found'

  if (isLoading) {
    return <LoadingSpinner position="center" label="Loading device" />
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }

  if (!device) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      <header className="mt-4 p-2">
        <h2 className="text-2xl font-bold text-primary-dark">
          Device {device.serial_number}
        </h2>
      </header>

      <hr className="border-outline/40" />

      <div className="grid gap-8 p-4 lg:grid-cols-2">
        <section className="flex flex-col gap-4">
          <h3 className="font-semibold text-black">Details</h3>

          <dl className="flex flex-col gap-4">
            <DeviceDetailRow label="Serial number" value={device.serial_number} />
            <DeviceDetailRow label="Notes" value={device.notes ?? BLANK_VALUE} />
            <DeviceDetailRow
              label="Status"
              value={<StatusBadge status={device.status} />}
            />
          </dl>
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-semibold text-black">History</h3>

          <div
            aria-label="Device history"
            className="min-h-48 rounded-xl border border-outline/40 bg-surface-muted/40 inset-shadow-sm"
          >
            <DeviceHistoryList
              histories={historyResource.data?.data ?? []}
              pagination={historyResource.data?.pagination ?? emptyPagination()}
              isLoading={historyResource.isLoading}
              error={historyResource.error}
              onPageChange={setHistoryPage}
            />
          </div>
        </section>
      </div>
    </div>
  )
}
